import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Icon, Button, Segment, Header } from 'semantic-ui-react';
import { DebugLog } from '../types';
import './DebugConsole.css';

interface DebugConsoleProps {
  className?: string;
}

export const DebugConsole: React.FC<DebugConsoleProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const debugLogs = useSelector((state: any) => state.checkIn?.debugLogs || []);
  const stats = useSelector((state: any) => state.checkIn?.stats);
  const searchIndex = useSelector((state: any) => state.checkIn?.searchIndex);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (isOpen && !isMinimized && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [debugLogs, isOpen, isMinimized]);

  const getIconName = (type: DebugLog['type']) => {
    switch (type) {
      case 'success': return 'check circle';
      case 'error': return 'exclamation triangle';
      case 'warning': return 'warning sign';
      default: return 'info circle';
    }
  };

  const getIconColor = (type: DebugLog['type']) => {
    switch (type) {
      case 'success': return 'green';
      case 'error': return 'red';
      case 'warning': return 'yellow';
      default: return 'blue';
    }
  };

  const formatMemoryUsage = () => {
    if (!searchIndex) return 'N/A';
    
    try {
      const indexStats = searchIndex.getStats();
      return `${indexStats.memoryEstimate}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const getPerformanceMetrics = () => {
    const lastFewLogs = debugLogs.slice(-10);
    const searchLogs = lastFewLogs.filter((log: DebugLog) => log.message.includes('Search'));
    const indexLogs = lastFewLogs.filter((log: DebugLog) => log.message.includes('Index') || log.message.includes('updated in index'));
    
    let avgSearchTime = 'N/A';
    let avgIndexTime = 'N/A';

    if (searchLogs.length > 0) {
      const searchTimes = searchLogs
        .map((log: DebugLog) => {
          const match = log.message.match(/\((\d+)ms\)/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter((time: number) => time > 0);
      
      if (searchTimes.length > 0) {
        avgSearchTime = `${Math.round(searchTimes.reduce((a: number, b: number) => a + b, 0) / searchTimes.length)}ms`;
      }
    }

    if (indexLogs.length > 0) {
      const indexTimes = indexLogs
        .map((log: DebugLog) => {
          const match = log.message.match(/\((\d+)ms\)/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter((time: number) => time > 0);
      
      if (indexTimes.length > 0) {
        avgIndexTime = `${Math.round(indexTimes.reduce((a: number, b: number) => a + b, 0) / indexTimes.length)}ms`;
      }
    }

    return { avgSearchTime, avgIndexTime };
  };

  const clearLogs = () => {
    // This would need to be connected to a Redux action to clear logs
  };

  if (!isOpen) {
    return (
      <div className={`debug-console-toggle ${className}`}>
        <Button
          circular
          icon="bug"
          size="large"
          color="grey"
          onClick={() => setIsOpen(true)}
          title="Open Debug Console"
        />
      </div>
    );
  }

  const { avgSearchTime, avgIndexTime } = getPerformanceMetrics();

  return (
    <div className={`debug-console ${isMinimized ? 'minimized' : ''} ${className}`}>
      <div className="debug-console-header">
        <Header as="h4" style={{ margin: 0, color: 'white' }}>
          <Icon name="bug" />
          Debug Console
        </Header>
        <div className="debug-console-controls">
          <Button
            size="mini"
            icon={isMinimized ? 'expand' : 'compress'}
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? 'Expand' : 'Minimize'}
          />
          <Button
            size="mini"
            icon="close"
            onClick={() => setIsOpen(false)}
            title="Close"
          />
        </div>
      </div>

      {!isMinimized && (
        <div className="debug-console-content">
          {/* Stats Section */}
          <Segment size="small" style={{ marginBottom: '10px' }}>
            <Header as="h5" style={{ marginBottom: '8px' }}>Current Stats</Header>
            <div className="debug-stats-grid">
              <div className="debug-stat">
                <strong>Sales:</strong> {stats?.totalSales || 0}
              </div>
              <div className="debug-stat">
                <strong>Entries:</strong> {stats?.totalEntries || 0}
              </div>
              <div className="debug-stat">
                <strong>Checked In:</strong> {stats?.checkedInEntries || 0}
              </div>
              <div className="debug-stat">
                <strong>Memory:</strong> {formatMemoryUsage()}
              </div>
              <div className="debug-stat">
                <strong>Search Time:</strong> {avgSearchTime}
              </div>
              <div className="debug-stat">
                <strong>Index Time:</strong> {avgIndexTime}
              </div>
            </div>
          </Segment>

          {/* Logs Section */}
          <div className="debug-logs-header">
            <Header as="h5" style={{ margin: 0 }}>
              Activity Log ({debugLogs.length})
            </Header>
            <Button
              size="mini"
              icon="trash"
              onClick={clearLogs}
              title="Clear logs"
            />
          </div>

          <div className="debug-logs-container">
            {debugLogs.length === 0 ? (
              <div className="debug-logs-empty">
                No debug logs yet...
              </div>
            ) : (
              debugLogs.map((log: DebugLog, index: number) => (
                <div key={index} className={`debug-log debug-log-${log.type}`}>
                  <Icon
                    name={getIconName(log.type)}
                    color={getIconColor(log.type)}
                    size="small"
                  />
                  <span className="debug-log-timestamp">[{log.timestamp}]</span>
                  <span className="debug-log-message">{log.message}</span>
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </div>
        </div>
      )}
    </div>
  );
};
